import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { PenLine, X } from "lucide-react";
import Button from "../ui/Button";
import { forPreview, printableText } from "../../util/printableText";
import { FOREWORD_BODY_FONT, FOREWORD_COLORS, FOREWORD_TITLE_FONT, forewordTextForPrint, normalizeForewordTitle } from "../../util/forewordPage";

/**
 * Preview's "Read up close" view of the dedication (docs/design/HANDOFF.md, "Dedication up close on
 * Preview": PreviewDedication, TabPreviewDedication, DeskPreviewDedication; decided 2026-10-26).
 *
 * Phones get a full screen: a white header with "Your dedication" and a Close X, the page as large as
 * fits, "This is exactly how it prints.", and a bottom bar with Edit dedication over Back to preview.
 * From tablet up it is a centred panel over the dimmed Preview, with the two buttons side by side.
 * Escape and Back to preview close it like the X. Faded with Headless UI's CSS transitions, as `Modal` is.
 *
 * The dedication is shown zoomed in on the words, not as the whole page (Alex, 2026-10-26: the point
 * is to read the message). The printed body is 12pt on an 8.75-inch page, too small on any screen,
 * and its fixed ~72-character lines can't be enlarged to a readable size on a phone. So the same text
 * (normalized like print: blank lines start a paragraph, single line breaks are kept) is set in the
 * page's style, frame, serif heading, honey rules and colours, at reading size and wrapped to the screen,
 * on a page of the printed shape (Alex, 2026-10-26).
 */

/** The heading and message in the printed page's style, at reading size. */
const DedicationText = ({ introKeyword, description }) => {
  const title = normalizeForewordTitle(printableText(introKeyword));
  const paragraphs = forewordTextForPrint(description)
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  const rule = <span aria-hidden="true" className="h-px w-10 flex-shrink-0" style={{ backgroundColor: FOREWORD_COLORS.deepHoney }} />;
  return (
    // The frame stays put; a message too long for the page scrolls inside it.
    <div className="flex h-full flex-col overflow-y-auto rounded-[3px] border px-5 py-8 text-center md:px-8 md:py-10" style={{ borderColor: FOREWORD_COLORS.frameStroke }}>
      <div className="my-auto flex flex-col items-center gap-4">
        {title ? (
          <p className="m-0 break-words font-heading text-[26px] leading-tight md:text-[30px]" style={{ color: FOREWORD_COLORS.darkCocoa, fontFamily: FOREWORD_TITLE_FONT }}>
            {forPreview(title)}
          </p>
        ) : null}
        {rule}
        <div className="flex flex-col gap-4 self-stretch text-[17px] leading-relaxed" style={{ color: FOREWORD_COLORS.warmSienna, fontFamily: FOREWORD_BODY_FONT }}>
          {paragraphs.map((p, i) => (
            <p key={i} className="m-0 whitespace-pre-line break-words">
              {forPreview(p)}
            </p>
          ))}
        </div>
        {rule}
      </div>
    </div>
  );
};
const DedicationUpClose = ({ open, onClose, onEdit, introKeyword, description }) => (
  <Dialog open={open} onClose={onClose} className="relative z-[70]">
    <DialogBackdrop
      transition
      className="fixed inset-0 hidden bg-[rgba(27,13,18,0.55)] transition-opacity duration-200 ease-out data-[closed]:opacity-0 md:block"
    />
    <div className="fixed inset-0 md:flex md:items-start md:justify-center md:overflow-y-auto md:px-6 md:py-10">
      <DialogPanel
        transition
        className="flex h-full w-full flex-col bg-md-pink-light-2 transition duration-200 ease-out data-[closed]:opacity-0 md:h-auto md:w-[476px] md:items-center md:gap-4 md:rounded-3xl md:px-7 md:pb-6 md:pt-5 md:shadow-[0_24px_60px_rgba(27,13,18,0.3)] md:data-[closed]:scale-95 lg:w-[456px]"
      >
        <div className="flex h-14 flex-shrink-0 items-center justify-between border-b border-[#F0E8DE] bg-white pl-4 pr-1.5 md:h-auto md:self-stretch md:border-0 md:bg-transparent md:p-0">
          <DialogTitle className="m-0 text-[17px] font-bold text-md-dark md:font-heading md:text-[26px] md:font-normal">Your dedication</DialogTitle>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-md text-md-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink md:-mr-2.5"
          >
            <X className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* A page at the printed shape (8.75 x 11.5), as large as the room left after the header, caption
            and buttons allows. */}
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3.5 p-4 md:contents">
          <div className="aspect-[2625/3450] w-[min(400px,100%,calc((100dvh_-_270px)*0.761))] flex-shrink-0 overflow-hidden rounded-md bg-white p-3 shadow-[0_8px_24px_rgba(60,40,30,0.16)] md:w-[min(420px,calc((100dvh_-_290px)*0.761))] md:p-4 lg:w-[min(400px,calc((100dvh_-_290px)*0.761))]">
            <DedicationText introKeyword={introKeyword} description={description} />
          </div>
          <p className="m-0 text-center text-sm text-md-muted">Shown larger so it's easy to read.</p>
        </div>

        <div className="flex flex-shrink-0 flex-col gap-1 border-t border-[#F0E8DE] bg-white px-4 pb-[max(30px,env(safe-area-inset-bottom))] pt-3 md:flex-row-reverse md:gap-3 md:self-stretch md:border-0 md:bg-transparent md:p-0">
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            onClick={onEdit}
            icon={<PenLine className="h-5 w-5" aria-hidden="true" />}
            className="md:min-h-[52px] md:flex-1 md:!text-base"
          >
            Edit dedication
          </Button>
          <Button variant="text" fullWidth onClick={onClose} className="!min-h-12 !text-base font-semibold md:!min-h-[52px] md:flex-1">
            Back to preview
          </Button>
        </div>
      </DialogPanel>
    </div>
  </Dialog>
);

export default DedicationUpClose;
